import React, { useState, useEffect, useRef } from 'react';
import { TailSpin } from 'react-loading-icons';
import AppTokenError from './application/AppTokenError';
import AppTokenInput from './application/AppTokenInput';
import AppIntro from './application/AppIntro';
import AppHousehold from './application/AppHousehold';
import AppPersonal from './application/AppPersonal';
import AppCoApplicants from './application/AppCoApplicants';
import AppConfirm from './application/AppConfirm';
import AppEnd from './application/AppEnd';
import AppStepIndicator from './application/AppStepIndicator';

const MIN_BIRTH_DATE_ISO = new Date(new Date().setFullYear(new Date().getFullYear() - 200)).toISOString().split('T')[0];
const MAX_BIRTH_DATE_ISO = new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0];

const emptyCoApplicant = () => ({
    salutation: '',
    forename: '',
    lastname: '',
    address: '',
    zip: '',
    city: '',
    phone: '',
    email: '',
    dateOfBirth: '',
    occupation: '',
    monthlyIncome: '',
    gwgTenant: '',
    debtInformation: null,
    debtUploading: false,
    debtUploadError: null,
    employer: { company: '', person: '', phone: '', occupation: '' },
    landlord: { name: '', phone: '' },
});

const Application = ({ clientUrl, token: initialToken }) => {
    const [loading, setLoading] = useState(!!initialToken);
    const [activeToken, setActiveToken] = useState(initialToken || '');
    const [tokenInputLoading, setTokenInputLoading] = useState(false);
    const [tokenInputError, setTokenInputError] = useState(null);
    const [tokenError, setTokenError] = useState(null);
    const [registration, setRegistration] = useState(null);
    const [step, setStep] = useState(0);
    const [err, setErr] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [draftId, setDraftId] = useState(null);
    const focusedTitleRef = useRef(null);
    const debtUploadIdRef = useRef(0);
    const coUploadIdsRef = useRef({});

    // Step 0: Wohnung
    const [parking, setParking] = useState('');
    const [moveinDate, setMoveinDate] = useState('');
    const [apartmentComments, setApartmentComments] = useState('');

    // Step 1: Haushalt
    const [motivation, setMotivation] = useState('');
    const [numberAdults, setNumberAdults] = useState(1);
    const [children, setChildren] = useState('');
    const [numberChildren, setNumberChildren] = useState('');
    const [birthyearsChildren, setBirthyearsChildren] = useState('');
    const [pets, setPets] = useState('');
    const [petsType, setPetsType] = useState('');

    // Step 2: Personalien Hauptbewerber
    const [salutation, setSalutation] = useState('');
    const [forename, setForename] = useState('');
    const [lastname, setLastname] = useState('');
    const [address, setAddress] = useState('');
    const [zip, setZip] = useState('');
    const [city, setCity] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [occupation, setOccupation] = useState('');
    const [monthlyIncome, setMonthlyIncome] = useState('');
    const [gwgTenant, setGwgTenant] = useState('');
    const [debtInformation, setDebtInformation] = useState(null);
    const [debtUploading, setDebtUploading] = useState(false);
    const [debtUploadError, setDebtUploadError] = useState(null);
    const [employerCompany, setEmployerCompany] = useState('');
    const [employerPerson, setEmployerPerson] = useState('');
    const [employerPhone, setEmployerPhone] = useState('');
    const [employerOccupation, setEmployerOccupation] = useState('');
    const [landlordName, setLandlordName] = useState('');
    const [landlordPhone, setLandlordPhone] = useState('');

    // Step 3: Mitbewerber
    const [coApplicants, setCoApplicants] = useState([]);

    useEffect(() => {
        if (initialToken) validateToken(initialToken);
    }, []);

    useEffect(() => {
        if (focusedTitleRef.current) focusedTitleRef.current.focus();
    }, [step]);


    const validateToken = async (t) => {
        try {
            const res = await fetch(`${clientUrl}/wp-json/apartment-finder/v1/validate-token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: t }),
            });
            if (res.status === 404) {
                setTokenError('Der Validierungsendpunkt wurde nicht gefunden. Bitte wenden Sie sich an den Administrator.');
                setLoading(false);
                return;
            }
            if (!res.ok) {
                setTokenError('Ihr Zugangslink ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.');
                setLoading(false);
                return;
            }
            setActiveToken(t);
            initFromRegistration(await res.json());
        } catch {
            setTokenError('Der Server ist nicht erreichbar. Bitte versuchen Sie es später erneut.');
            setLoading(false);
        }
    };

    const handleTokenInputSubmit = async (enteredToken) => {
        setTokenInputLoading(true);
        setTokenInputError(null);
        try {
            const res = await fetch(`${clientUrl}/wp-json/apartment-finder/v1/validate-token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: enteredToken }),
            });
            if (res.status === 404) {
                setTokenInputError('Der Validierungsendpunkt wurde nicht gefunden. Bitte wenden Sie sich an den Administrator.');
                setTokenInputLoading(false);
                return;
            }
            if (!res.ok) {
                setTokenInputError('Ungültiger Zugangscode. Bitte überprüfen Sie Ihre Eingabe.');
                setTokenInputLoading(false);
                return;
            }
            setActiveToken(enteredToken);
            setLoading(true);
            setTokenInputLoading(false);
            initFromRegistration(await res.json());
        } catch {
            setTokenInputError('Der Server ist nicht erreichbar. Bitte versuchen Sie es später erneut.');
            setTokenInputLoading(false);
        }
    };

    const isValidBirthDate = (value) => !!value && value >= MIN_BIRTH_DATE_ISO && value <= MAX_BIRTH_DATE_ISO;
    const isValidZip = (value) => /^\d{4,5}$/.test(value.trim());
    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

    const focusFirstError = () => {
        const field = document.querySelector('#af_application_inner .is-invalid');
        if (!field) return;
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.focus({ preventScroll: true });
    };

    const buildFormData = () => {
        const fd = new FormData();
        fd.append('token', activeToken);
        fd.append('parking', parking);
        fd.append('moveinDate', moveinDate);
        fd.append('apartmentComments', apartmentComments);
        fd.append('motivation', motivation);
        fd.append('numberAdults', numberAdults);
        fd.append('children', children);
        fd.append('numberChildren', numberChildren);
        fd.append('birthyearsChildren', birthyearsChildren.trim());
        fd.append('pets', pets);
        fd.append('petsType', petsType.trim());
        fd.append('salutation', salutation);
        fd.append('forename', forename.trim());
        fd.append('lastname', lastname.trim());
        fd.append('address', address.trim());
        fd.append('zip', zip.trim());
        fd.append('city', city.trim());
        fd.append('phone', phone.trim());
        fd.append('email', email.trim());
        fd.append('dateOfBirth', dateOfBirth);
        fd.append('occupation', occupation.trim());
        fd.append('monthlyIncome', monthlyIncome);
        fd.append('gwgTenant', gwgTenant);
        fd.append('employerCompany', employerCompany.trim());
        fd.append('employerPerson', employerPerson.trim());
        fd.append('employerPhone', employerPhone.trim());
        fd.append('employerOccupation', employerOccupation.trim());
        fd.append('landlordName', landlordName.trim());
        fd.append('landlordPhone', landlordPhone.trim());
        if (debtInformation?.attachmentId) fd.append('debtInformationId', debtInformation.attachmentId);
        coApplicants.forEach((ca, i) => {
            const p = `coApplicants[${i}]`;
            fd.append(`${p}[salutation]`, ca.salutation);
            fd.append(`${p}[forename]`, ca.forename.trim());
            fd.append(`${p}[lastname]`, ca.lastname.trim());
            fd.append(`${p}[address]`, ca.address.trim());
            fd.append(`${p}[zip]`, ca.zip.trim());
            fd.append(`${p}[city]`, ca.city.trim());
            fd.append(`${p}[phone]`, ca.phone.trim());
            fd.append(`${p}[email]`, ca.email.trim());
            fd.append(`${p}[dateOfBirth]`, ca.dateOfBirth);
            fd.append(`${p}[occupation]`, ca.occupation.trim());
            fd.append(`${p}[monthlyIncome]`, ca.monthlyIncome);
            fd.append(`${p}[gwgTenant]`, ca.gwgTenant);
            fd.append(`${p}[employerCompany]`, (ca.employer?.company || '').trim());
            fd.append(`${p}[employerPerson]`, (ca.employer?.person || '').trim());
            fd.append(`${p}[employerPhone]`, (ca.employer?.phone || '').trim());
            fd.append(`${p}[employerOccupation]`, (ca.employer?.occupation || '').trim());
            fd.append(`${p}[landlordName]`, (ca.landlord?.name || '').trim());
            fd.append(`${p}[landlordPhone]`, (ca.landlord?.phone || '').trim());
            if (ca.debtInformation?.attachmentId) fd.append(`${p}[debtInformationId]`, ca.debtInformation.attachmentId);
        });
        return fd;
    };

    const saveDraft = async () => {
        const fd = buildFormData();
        if (draftId) fd.append('draftId', draftId);
        const res = await fetch(`${clientUrl}/wp-json/apartment-finder/v1/applications/draft`, {
            method: 'POST',
            body: fd,
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (data?.draftId) setDraftId(data.draftId);
    };

    const initFromRegistration = (reg) => {
        setRegistration(reg);
        if (reg.submitted) {
            setStep(5);
            setLoading(false);
            return;
        }
        if (reg.draft) {
            setDraftId(reg.draft.id);
            restoreDraft(reg.draft);
        } else {
            prefillFromRegistration(reg);
        }
        setLoading(false);
    };

    const prefillFromRegistration = (reg) => {
        if (reg.forename) setForename(reg.forename);
        if (reg.lastname) setLastname(reg.lastname);
        if (reg.email) setEmail(reg.email);
        if (reg.phone) setPhone(reg.phone);
        if (reg.address) setAddress(reg.address);
        if (reg.zip) setZip(reg.zip);
        if (reg.city) setCity(reg.city);
        if (reg.motivation) setMotivation(reg.motivation);
        if (reg.comments) setApartmentComments(reg.comments);
        if (reg.gwgTenant !== undefined) setGwgTenant(reg.gwgTenant ? 'yes' : 'no');
        if (reg.cats && reg.cats !== 'no') setPets('yes');
        if (reg.residents > 1) {
            setNumberAdults(reg.residents);
            setCoApplicants(Array.from({ length: reg.residents - 1 }, (_, i) => ({
                ...emptyCoApplicant(),
                forename: reg.flatmates?.[i]?.forename || '',
                lastname: reg.flatmates?.[i]?.lastname || '',
            })));
        }
    };

    const restoreDraft = (d) => {
        const s = (v) => v ?? '';
        setParking(s(d.parking));
        setMoveinDate(s(d.moveinDate));
        setApartmentComments(s(d.apartmentComments));
        setMotivation(s(d.motivation));
        setNumberAdults(Number(d.numberAdults) || 1);
        setChildren(s(d.children));
        setNumberChildren(s(d.numberChildren));
        setBirthyearsChildren(s(d.birthyearsChildren));
        setPets(s(d.pets));
        setPetsType(s(d.petsType));
        setSalutation(s(d.salutation));
        setForename(s(d.forename));
        setLastname(s(d.lastname));
        setAddress(s(d.address));
        setZip(s(d.zip));
        setCity(s(d.city));
        setPhone(s(d.phone));
        setEmail(s(d.email));
        setDateOfBirth(s(d.dateOfBirth));
        setOccupation(s(d.occupation));
        setMonthlyIncome(s(d.monthlyIncome));
        setGwgTenant(s(d.gwgTenant));
        setEmployerCompany(s(d.employerCompany));
        setEmployerPerson(s(d.employerPerson));
        setEmployerPhone(s(d.employerPhone));
        setEmployerOccupation(s(d.employerOccupation));
        setLandlordName(s(d.landlordName));
        setLandlordPhone(s(d.landlordPhone));
        setDebtInformation(d.debtInformation ?? null);
        setCoApplicants((d.coApplicants || []).map(ca => ({
            salutation: s(ca.salutation), forename: s(ca.forename), lastname: s(ca.lastname),
            address: s(ca.address), zip: s(ca.zip), city: s(ca.city),
            phone: s(ca.phone), email: s(ca.email), dateOfBirth: s(ca.dateOfBirth),
            occupation: s(ca.occupation), monthlyIncome: s(ca.monthlyIncome), gwgTenant: s(ca.gwgTenant),
            debtInformation: ca.debtInformation ?? null,
            debtUploading: false, debtUploadError: null,
            employer: { company: s(ca.employerCompany), person: s(ca.employerPerson), phone: s(ca.employerPhone), occupation: s(ca.employerOccupation) },
            landlord: { name: s(ca.landlordName), phone: s(ca.landlordPhone) },
        })));
    };

    const setters = {
        parking: setParking, moveinDate: setMoveinDate, apartmentComments: setApartmentComments,
        motivation: setMotivation, children: setChildren, numberChildren: setNumberChildren,
        birthyearsChildren: setBirthyearsChildren, pets: setPets, petsType: setPetsType,
        salutation: setSalutation, forename: setForename, lastname: setLastname,
        address: setAddress, zip: setZip, city: setCity, phone: setPhone, email: setEmail,
        dateOfBirth: setDateOfBirth, occupation: setOccupation, monthlyIncome: setMonthlyIncome,
        gwgTenant: setGwgTenant,
        employerCompany: setEmployerCompany, employerPerson: setEmployerPerson,
        employerPhone: setEmployerPhone, employerOccupation: setEmployerOccupation,
        landlordName: setLandlordName, landlordPhone: setLandlordPhone,
    };

    const handleChange = (input) => (e) => {
        const value = e.target.value;

        if (input === 'numberAdults') {
            const n = parseInt(value, 10);
            setNumberAdults(n);
            setCoApplicants(prev => {
                const needed = n - 1;
                if (prev.length < needed) {
                    return [...prev, ...Array(needed - prev.length).fill(null).map(emptyCoApplicant)];
                }
                return prev.slice(0, needed);
            });
            return;
        }

        if (input === 'children' && value === 'no') {
            setNumberChildren('');
            setBirthyearsChildren('');
        }

        if (input === 'pets' && value === 'no') {
            setPetsType('');
        }

        if (setters[input]) setters[input](value);
    };

    const uploadFile = async (file) => {
        const fd = new FormData();
        fd.append('token', activeToken);
        fd.append('file', file);
        const res = await fetch(`${clientUrl}/wp-json/apartment-finder/v1/upload`, {
            method: 'POST',
            body: fd,
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || 'Upload fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
        return await res.json();
    };

    const handleDebtFileChange = async (file) => {
        setDebtInformation(null);
        setDebtUploadError(null);
        if (!file) return;
        const uploadId = ++debtUploadIdRef.current;
        setDebtUploading(true);
        try {
            const { attachmentId, filename } = await uploadFile(file);
            if (uploadId !== debtUploadIdRef.current) return;
            setDebtInformation({ name: filename, attachmentId });
        } catch (e) {
            if (uploadId !== debtUploadIdRef.current) return;
            setDebtUploadError(e.message);
        } finally {
            if (uploadId === debtUploadIdRef.current) setDebtUploading(false);
        }
    };

    const handleCoApplicantChange = (index) => (field) => (e) => {
        const value = e.target.value;
        setCoApplicants(prev => prev.map((ca, i) => {
            if (i !== index) return ca;
            if (field === 'employerCompany') return { ...ca, employer: { ...ca.employer, company: value } };
            if (field === 'employerPerson') return { ...ca, employer: { ...ca.employer, person: value } };
            if (field === 'employerPhone') return { ...ca, employer: { ...ca.employer, phone: value } };
            if (field === 'employerOccupation') return { ...ca, employer: { ...ca.employer, occupation: value } };
            if (field === 'landlordName') return { ...ca, landlord: { ...ca.landlord, name: value } };
            if (field === 'landlordPhone') return { ...ca, landlord: { ...ca.landlord, phone: value } };
            if (field === 'gwgTenant' && value === 'yes') return { ...ca, gwgTenant: value, debtInformation: null };
            return { ...ca, [field]: value };
        }));
    };

    const handleCoApplicantFile = (index) => async (file) => {
        const uploadId = (coUploadIdsRef.current[index] || 0) + 1;
        coUploadIdsRef.current[index] = uploadId;
        setCoApplicants(prev => prev.map((ca, i) => i === index
            ? { ...ca, debtInformation: null, debtUploadError: null, debtUploading: !!file }
            : ca));
        if (!file) return;
        try {
            const { attachmentId, filename } = await uploadFile(file);
            if (coUploadIdsRef.current[index] !== uploadId) return;
            setCoApplicants(prev => prev.map((ca, i) => i === index
                ? { ...ca, debtInformation: { name: filename, attachmentId }, debtUploading: false }
                : ca));
        } catch (e) {
            if (coUploadIdsRef.current[index] !== uploadId) return;
            setCoApplicants(prev => prev.map((ca, i) => i === index
                ? { ...ca, debtUploadError: e.message, debtUploading: false }
                : ca));
        }
    };

    const validateStep = (s) => {
        const errors = {};
        if (s === 1) {
            if (!motivation.trim()) errors.motivation = 'Bitte geben Sie den Grund des Wohnungswechsels an.';
            if (!children) errors.children = 'Bitte geben Sie an, ob Kinder einziehen.';
            if (children === 'yes' && !numberChildren) errors.numberChildren = 'Bitte geben Sie die Anzahl der Kinder an.';
            if (!pets) errors.pets = 'Bitte geben Sie an, ob Sie Haustiere besitzen.';
            if (pets === 'yes' && !petsType.trim()) errors.petsType = 'Bitte geben Sie die Art/Rasse Ihrer Haustiere an.';
        }
        if (s === 2) {
            if (!forename.trim()) errors.forename = 'Bitte geben Sie Ihren Vornamen an.';
            if (!lastname.trim()) errors.lastname = 'Bitte geben Sie Ihren Nachnamen an.';
            if (!address.trim()) errors.address = 'Bitte geben Sie Ihre Adresse an.';
            if (!zip.trim()) errors.zip = 'Bitte geben Sie Ihre Postleitzahl an.';
            else if (!isValidZip(zip)) errors.zip = 'Bitte geben Sie eine gültige Postleitzahl an.';
            if (!phone.trim()) errors.phone = 'Bitte geben Sie Ihre Telefonnummer an.';
            if (!email.trim()) errors.email = 'Bitte geben Sie Ihre E-Mail-Adresse an.';
            else if (!isValidEmail(email)) errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse an.';
            if (!dateOfBirth) errors.dateOfBirth = 'Bitte ein gültiges Geburtsdatum eingeben (TT.MM.JJJJ) oder über den Kalender wählen.';
            else if (!isValidBirthDate(dateOfBirth)) errors.dateOfBirth = 'Bitte überprüfen Sie Ihr Geburtsdatum.';
            if (!monthlyIncome) errors.monthlyIncome = 'Bitte geben Sie Ihr monatliches Bruttoeinkommen an.';
            if (!gwgTenant) errors.gwgTenant = 'Bitte geben Sie an, ob Sie aktuell bei der GWG wohnen.';
            if (gwgTenant === 'no' && !debtInformation?.attachmentId) errors.debtInformation = 'Bitte laden Sie Ihre Betreibungsauskunft hoch.';
        }
        if (s === 3) {
            const caErrors = coApplicants.map((ca) => {
                const e = {};
                if (!ca.forename.trim()) e.forename = 'Pflichtfeld.';
                if (!ca.lastname.trim()) e.lastname = 'Pflichtfeld.';
                if (!ca.address.trim()) e.address = 'Pflichtfeld.';
                if (!ca.zip.trim()) e.zip = 'Pflichtfeld.';
                else if (!isValidZip(ca.zip)) e.zip = '4-5 Ziffern.';
                if (!ca.phone.trim()) e.phone = 'Pflichtfeld.';
                if (ca.email.trim() && !isValidEmail(ca.email)) e.email = 'Ungültige E-Mail.';
                if (!ca.dateOfBirth) e.dateOfBirth = 'Pflichtfeld.';
                else if (!isValidBirthDate(ca.dateOfBirth)) e.dateOfBirth = 'Bitte überprüfen.';
                if (!ca.monthlyIncome) e.monthlyIncome = 'Pflichtfeld.';
                if (!ca.gwgTenant) e.gwgTenant = 'Pflichtfeld.';
                if (ca.gwgTenant === 'no' && !ca.debtInformation?.attachmentId) e.debtInformation = 'Pflichtfeld.';
                return e;
            });
            if (caErrors.some(e => Object.keys(e).length > 0)) errors.coApplicants = caErrors;
        }
        return errors;
    };

    const anyUploading = debtUploading || coApplicants.some(ca => ca.debtUploading);

    const nextStep = async () => {
        if (anyUploading) return;
        const errors = validateStep(step);
        if (Object.keys(errors).length) {
            setErr(errors);
            requestAnimationFrame(focusFirstError);
            return;
        }
        setErr({});
        await saveDraft();
        const next = step === 2 && numberAdults <= 1 ? 4 : step + 1;
        setStep(next);
    };

    const prevStep = async () => {
        setErr({});
        await saveDraft();
        const prev = step === 4 && numberAdults <= 1 ? 2 : step - 1;
        setStep(prev);
    };

    const submitApplication = async () => {
        setSubmitting(true);
        setSubmitError(null);

        try {
            const fd = buildFormData();
            if (draftId) fd.append('draftId', draftId);

            const res = await fetch(`${clientUrl}/wp-json/apartment-finder/v1/applications`, {
                method: 'POST',
                body: fd,
            });

            if (res.status === 409) {
                setStep(5);
                return;
            }

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                setSubmitError(errData.message?.startsWith('Missing required field')
                    ? 'Bitte alle Pflichtfelder ausfüllen.'
                    : errData.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
                setSubmitting(false);
                return;
            }

            setStep(5);
        } catch {
            setSubmitError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center mt-5">
                <TailSpin stroke="#000" />
            </div>
        );
    }

    if (!activeToken) return <AppTokenInput onSubmit={handleTokenInputSubmit} loading={tokenInputLoading} error={tokenInputError} />;

    if (tokenError) return <AppTokenError message={tokenError} />;

    const values = {
        parking, moveinDate, apartmentComments,
        motivation, numberAdults, children, numberChildren, birthyearsChildren, pets, petsType,
        salutation, forename, lastname, address, zip, city, phone, email, dateOfBirth,
        occupation, monthlyIncome, gwgTenant, debtInformation, debtUploading, debtUploadError,
        employerCompany, employerPerson, employerPhone, employerOccupation,
        landlordName, landlordPhone,
        coApplicants,
    };

    const shared = { err, values, focusedTitleRef, anyUploading, minBirthDate: MIN_BIRTH_DATE_ISO, maxBirthDate: MAX_BIRTH_DATE_ISO };

    return (
        <div id="af_application_inner">
            {step < 5 && <AppStepIndicator step={step} numberAdults={numberAdults} />}
            {step === 0 && <AppIntro {...shared} registration={registration} handleChange={handleChange} nextStep={nextStep} />}
            {step === 1 && <AppHousehold {...shared} handleChange={handleChange} prevStep={prevStep} nextStep={nextStep} />}
            {step === 2 && <AppPersonal {...shared} handleChange={handleChange} handleDebtFileChange={handleDebtFileChange} prevStep={prevStep} nextStep={nextStep} />}
            {step === 3 && <AppCoApplicants {...shared} handleCoApplicantChange={handleCoApplicantChange} handleCoApplicantFile={handleCoApplicantFile} prevStep={prevStep} nextStep={nextStep} />}
            {step === 4 && <AppConfirm {...shared} registration={registration} submitting={submitting} submitError={submitError} prevStep={prevStep} onSubmit={submitApplication} />}
            {step === 5 && <AppEnd focusedTitleRef={focusedTitleRef} />}
        </div>
    );
};

export default Application;
